import { DOCUMENT } from '@angular/common';
import { AfterViewInit, inject, Directive, ElementRef, NgZone, OnDestroy, Renderer2 } from '@angular/core';

/** Keep a synchronized table scrollbar at the bottom of the viewport. */
@Directive({
  selector: '[appBottomScrollbar]',
  standalone: true,
  host: { class: '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden' },
})
export class BottomScrollbarDirective implements AfterViewInit, OnDestroy {
  private scrollbar: HTMLDivElement;
  private spacer: HTMLDivElement;
  private resizeObserver: ResizeObserver;
  private mutationObserver: MutationObserver;
  private listeners: Array<() => void> = [];
  private frame: number | null = null;
  private readonly document = inject(DOCUMENT);

  constructor(
    private element: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    const target = this.element.nativeElement;
    this.scrollbar = this.renderer.createElement('div');
    this.spacer = this.renderer.createElement('div');
    // Escape ancestor overflow and transforms so fixed positioning uses the viewport.
    this.scrollbar.className = 'fixed bottom-0 z-10 overflow-x-scroll overflow-y-hidden bg-white';
    this.spacer.className = 'h-px';
    this.scrollbar.tabIndex = 0;
    this.scrollbar.setAttribute('role', 'region');
    this.scrollbar.setAttribute('aria-label', 'Scroll table horizontally');
    this.renderer.appendChild(this.scrollbar, this.spacer);
    this.renderer.appendChild(this.document.body, this.scrollbar);

    this.zone.runOutsideAngular(() => {
      this.listeners.push(
        this.renderer.listen(target, 'scroll', () => {
          if (this.scrollbar.scrollLeft !== target.scrollLeft) {
            this.scrollbar.scrollLeft = target.scrollLeft;
          }
        }),
        this.renderer.listen(this.scrollbar, 'scroll', () => {
          if (target.scrollLeft !== this.scrollbar.scrollLeft) {
            target.scrollLeft = this.scrollbar.scrollLeft;
          }
        }),
      );
      const scheduleUpdate = () => this.scheduleUpdate();
      // Capture scrolls from nested page containers as well as the document.
      this.document.addEventListener('scroll', scheduleUpdate, true);
      this.listeners.push(
        () => this.document.removeEventListener('scroll', scheduleUpdate, true),
        this.renderer.listen('window', 'resize', scheduleUpdate),
      );
      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.observeContent();
      this.mutationObserver = new MutationObserver(() => {
        this.observeContent();
        this.scheduleUpdate();
      });
      this.mutationObserver.observe(target, { childList: true, subtree: true, characterData: true, attributes: true });
      this.update();
    });
  }

  private observeContent(): void {
    this.resizeObserver.disconnect();
    for (let ancestor = this.element.nativeElement; ancestor; ancestor = ancestor.parentElement) {
      this.resizeObserver.observe(ancestor);
    }
    // Table width can change without resizing its overflow container (columns, data, fonts).
    for (const child of Array.from(this.element.nativeElement.children)) {
      this.resizeObserver.observe(child);
    }
  }

  private scheduleUpdate(): void {
    if (this.frame !== null) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  private update(): void {
    const target = this.element.nativeElement;
    const viewport = this.document.documentElement;
    const rect = target.getBoundingClientRect();
    let left = Math.max(0, rect.left + target.clientLeft);
    let right = Math.min(viewport.clientWidth, rect.left + target.clientLeft + target.clientWidth);
    let top = Math.max(0, rect.top);
    let bottom = Math.min(viewport.clientHeight, rect.bottom);

    // A table can be outside a nested scroll container despite intersecting the viewport.
    for (let ancestor = target.parentElement; ancestor; ancestor = ancestor.parentElement) {
      const style = this.document.defaultView.getComputedStyle(ancestor);
      const bounds = ancestor.getBoundingClientRect();
      if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
        left = Math.max(left, bounds.left + ancestor.clientLeft);
        right = Math.min(right, bounds.left + ancestor.clientLeft + ancestor.clientWidth);
      }
      if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
        top = Math.max(top, bounds.top + ancestor.clientTop);
        bottom = Math.min(bottom, bounds.top + ancestor.clientTop + ancestor.clientHeight);
      }
    }

    const width = Math.max(0, right - left);
    const overflow = target.scrollWidth - target.clientWidth;
    this.scrollbar.style.display = overflow > 0 && width > 0 && bottom > top ? 'block' : 'none';
    this.scrollbar.style.left = `${left}px`;
    this.scrollbar.style.width = `${width}px`;
    // Preserve the table's scroll range when its visible width is clipped.
    const rangeWidth = `${overflow + width}px`;
    if (this.spacer.style.width !== rangeWidth) {
      this.spacer.style.width = rangeWidth;
      this.scrollbar.scrollLeft = target.scrollLeft;
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.listeners.forEach(unlisten => unlisten());
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
    }
    this.scrollbar?.remove();
  }
}
